/**
 * PASSO 2 — PARÂMETROS DA NEGOCIAÇÃO. Como se pesa e quanto vale cada lote.
 *
 * 3D (test drive de 06/10/2026) — FICHA ABERTA GRAVA SOZINHA: cada mudança
 * vai para a ficha depois de uma pausa curta, e o botão só leva adiante
 * («Próximo ›», com o sinal «✓ salvo» ao lado). O gestor ia e voltava entre as
 * abas e o «Salvar e seguir» à vista deixava a dúvida «será que está salvo?».
 * Lote sem categoria não é gravado (fica só na tela até ser escolhida).
 *
 * FICHA ENCERRADA não grava sozinha: corrigir a negociação gera uma NOVA
 * VERSÃO do romaneio, e isso continua sendo um botão explícito («Salvar nova
 * versão do romaneio»). O histórico guarda o que mudou e como era antes. Os
 * pesos não são tocados — corrigir o rendimento digitado errado não obriga a
 * pesar de novo.
 *
 * 3D · DESCONTOS POR LOTE: o financeiro e o funrural moram dentro de cada lote
 * («Descontos ▸»): vacas a 2% + 1,63%, bezerros só com o funrural. O lote novo
 * copia os do lote anterior. A seção de descontos da ficha saiu da tela.
 *
 * 3B · DESTINOS (PORTEIRAS) também se montam aqui, com calma, antes do curral:
 * «isso não se decide com cliques no curral». Na pesagem só se toca a porteira.
 * Mudar porteira NÃO gera versão do romaneio: o vendedor recebe um romaneio só,
 * com todos os animais, e a porteira não aparece para ele.
 * 3D: o NOME da porteira sai de uma lista, para não duplicar o mesmo lugar —
 * clientes (produtores com o marcador `cliente`) e locais próprios do gestor
 * (produtores com o marcador `meuLocal`). Nome novo entra no cadastro ao sair
 * da tela (ou ao salvar a versão), uma vez só.
 *
 * 3F · DOIS JEITOS DA PORTEIRA DE CLIENTE: «Cliente (intermediação)» — o
 * cliente paga o vendedor direto, o gestor recebe comissão — e «Revenda» — o
 * gestor paga o vendedor e vende ao comprador final com ágio. A Revenda ganha
 * a seção «Preço de venda», com os MESMOS lotes da compra: cada um recebe a
 * base, o preço, o rendimento e os descontos da venda. Só o gestor monta (a
 * Consulta vê; o comprador nem vê a seção). O preço de venda NÃO entra na
 * ficha (que sincroniza para o comprador): mora na tabela da migração 012, e
 * se grava no botão «Salvar preço de venda», sem versão nova do romaneio.
 *
 * R1C (revisão de UX de 06/10/2026):
 *  - RENDIMENTO SÓ DE 40 A 60%. «5252%» era aceito e em branco virava 50% sem
 *    avisar. Fora da faixa (ou em branco, no lote por arroba): aviso vermelho
 *    embaixo do campo e o lote NÃO é gravado até corrigir. Escolher a categoria
 *    traz o rendimento padrão dela (Ajustes);
 *  - o preço diz a unidade («R$ por arroba»), não mais «opcional»;
 *  - «Quebra (%)» opcional por lote, tirada do peso antes da arroba;
 *  - os textos de ajuda mudam com o papel: o comprador não lê sobre comissão,
 *    ágio e revenda, e não vê «+ Revenda» (usa `pode('verVenda')`);
 *  - ao gravar, os lotes da ficha gravada que a tela não conhece FICAM: um
 *    «+ Novo lote» criado no curral por outro aparelho pode ter descido
 *    enquanto esta tela estava aberta, e sumir com ele levaria junto as contas
 *    dos registros dele.
 */
import { useEffect, useRef, useState } from 'react'
import { acharProdutorPeloNome, guardarProdutor, lerEstado, mudar, mudarFicha, produtorVazio, usarEstado, type Estado } from '../dados/armazem'
import { diferencas, negociacaoDe } from '../dados/acoes'
import { aplicarNovaVersao, calcular, centavos, descontosDoLote, nomeDoLote, problemaDoRendimento, quebraDoLote, rendimentoPadrao, textoDosDescontos, type Descontos } from '../dominio/contas'
import { nomeDaPorteira, porteiraDe, porteirasDa } from '../dominio/destinos'
import { linhasDeDesconto } from '../dominio/exportar'
import { agora, kg, lerNumero, nomeParecido, normalizar, novoId, paraCampo, reais } from '../dominio/numeros'
import { BASES, NOME_FINANCEIRO, NOME_FUNRURAL, TIPOS_DE_PORTEIRA, type Base, type Ficha, type Forma, type Lote, type Porteira, type Produtor, type TipoDePorteira } from '../dominio/tipos'
import { avisar, AvisoDeNome, Campo, confirmar, Dica, Escolha, Segmento, SinalDeSalvo, Tarja, usarGravacaoAutomatica, type Ir } from '../ui'
import { usarPapel, usarPode } from '../dados/sessao'
import { lancarVenda, usarPagamentos, vendaDaPorteira } from '../dados/pagamentos'
import { loteDeVendaPadrao } from '../dominio/revenda'
import type { LoteDeVenda } from '../dominio/tipos'
import { usarCategoriasDoEstoque } from './Catira'

interface LoteRascunho { id: string; categoria: string; base: Base; preco: string; rendimento: string; fin: string; fun: string; quebra?: string }

/** O lote na tela. Lote de ficha antiga (sem descontos próprios) mostra os da ficha, que são os que valem para ele. */
function paraRascunho(l: Lote, f: Pick<Ficha, 'financeiroPct' | 'funruralPct'>): LoteRascunho {
  const d = descontosDoLote(l, f)
  return {
    id: l.id, categoria: l.categoria, base: l.base, preco: paraCampo(l.preco), rendimento: paraCampo(l.rendimento), fin: paraCampo(d.financeiroPct || null), fun: paraCampo(d.funruralPct || null),
    quebra: paraCampo(quebraDoLote(l) || null),
  }
}

/**
 * R1C · O que impede o lote de ser gravado: o rendimento, no lote por arroba,
 * fora de 40 a 60% ou em branco. Os outros lotes não usam rendimento.
 */
export function problemaDoLote(r: Pick<LoteRascunho, 'base' | 'rendimento' | 'quebra'>): string | null {
  if (r.base === 'arroba') {
    const p = problemaDoRendimento(lerNumero(r.rendimento))
    if (p) return p
  }
  const q = lerNumero(r.quebra ?? '')
  if (q !== null && (q < 0 || q >= 20)) return `Quebra de ${paraCampo(q)}% não serve. Use de 0 a 20%.`
  return null
}

/** Os descontos de um lote da tela, em número (campo vazio = 0). */
function descontosDoRascunho(r: Pick<LoteRascunho, 'fin' | 'fun'>): Descontos {
  return { financeiroPct: lerNumero(r.fin) ?? 0, funruralPct: lerNumero(r.fun) ?? 0 }
}

/**
 * O lote para gravar. Só se chama com `problemaDoLote` vazio: no lote por
 * arroba o rendimento já foi conferido; nos outros ele não entra na conta e,
 * em branco, fica o padrão da categoria (`rendimentoReserva`).
 */
export function loteDoRascunho(r: LoteRascunho, rendimentoReserva = 50): Lote {
  const preco = lerNumero(r.preco)
  const rend = lerNumero(r.rendimento)
  const quebra = lerNumero(r.quebra ?? '')
  // 3D: o lote gravado sempre leva os seus descontos — o da ficha fica só para lote antigo
  return {
    id: r.id, categoria: r.categoria.trim(), base: r.base, preco: preco !== null && preco > 0 ? preco : null,
    rendimento: rend !== null && !problemaDoRendimento(rend) ? rend : rendimentoReserva,
    ...descontosDoRascunho(r),
    // R1C: a quebra só entra no documento quando existe (ficha antiga fica igual)
    ...(quebra !== null && quebra > 0 ? { quebraPct: quebra } : {}),
  }
}

/** R1C · «R$ por arroba», «R$ por kg», «R$ por cabeça» — o que o campo de preço espera. */
const UNIDADE_DO_PRECO: Record<Base, string> = { arroba: 'R$ por arroba', kg: 'R$ por kg', cabeca: 'R$ por cabeça' }

/** O cartão de um lote — usado aqui e na janela "+ Novo lote" da pesagem. */
export function EditorDeLote(p: {
  lote: LoteRascunho; mudar: (l: LoteRascunho) => void; categorias: string[]; remover?: () => void; usados?: number
  /** R1C: o rendimento padrão de cada categoria (Ajustes) */
  rendimentoDe?: (categoria: string) => number
  /** R2D: o nome de cada categoria na lista (Venda do meu estoque: «Bezerro — 32 cabeças no estoque») */
  rotuloDaCategoria?: (categoria: string) => string
}) {
  const l = p.lote
  const cats = p.categorias.includes(l.categoria) || !l.categoria ? p.categorias : [...p.categorias, l.categoria]
  // 3D: os descontos ficam fechados num botãozinho — quase sempre o lote copia os do anterior
  const [descontos, setDescontos] = useState(false)
  const problema = problemaDoLote(l)
  const temQuebra = (lerNumero(l.quebra ?? '') ?? 0) > 0
  /**
   * R1C: escolher a categoria traz o rendimento padrão dela — mas só se o
   * campo ainda tem o padrão (da categoria anterior, ou o geral) ou está em
   * branco. Rendimento digitado à mão nunca é trocado por baixo.
   */
  function escolherCategoria(v: string) {
    const atual = lerNumero(l.rendimento)
    const anterior = l.categoria && p.rendimentoDe ? p.rendimentoDe(l.categoria) : null
    const padraoGeral = p.rendimentoDe ? p.rendimentoDe('') : null
    const intocado = atual === null || atual === anterior || atual === padraoGeral
    p.mudar({ ...l, categoria: v, ...(intocado && v && p.rendimentoDe ? { rendimento: paraCampo(p.rendimentoDe(v)) } : {}) })
  }
  return (
    <div className="lote">
      <div className="linha">
        <Escolha rotulo="Categoria" valor={l.categoria} mudar={escolherCategoria}
          opcoes={[['', 'escolha…'], ...cats.map((c): [string, string] => [c, p.rotuloDaCategoria ? p.rotuloDaCategoria(c) : c])]} />
        <Escolha rotulo="Base do preço" valor={l.base} mudar={(v) => p.mudar({ ...l, base: v })}
          opcoes={[['arroba', 'Por arroba'], ['kg', 'Por kg vivo'], ['cabeca', 'Por cabeça']]} />
      </div>
      <div className="linha">
        <Campo rotulo={`Preço (${BASES[l.base].unidade})`} valor={l.preco} mudar={(v) => p.mudar({ ...l, preco: v })} modo="decimal" dica={UNIDADE_DO_PRECO[l.base]} />
        {l.base === 'arroba'
          ? <Campo rotulo="Rendimento (%)" valor={l.rendimento} mudar={(v) => p.mudar({ ...l, rendimento: v })} modo="decimal" dica="de 48 a 56" />
          : <div className="campo"><span>&nbsp;</span><div className="mini-dica">{l.base === 'cabeca' ? 'Por cabeça: peso opcional, sem rendimento.' : 'Por kg: sem rendimento.'}</div></div>}
      </div>
      {problema && <div className="alerta" role="alert">{problema} Este lote só é gravado depois de corrigir.</div>}
      {l.base !== 'cabeca' && (
        <div className="linha">
          <Campo rotulo="Quebra (%)" valor={l.quebra ?? ''} mudar={(v) => p.mudar({ ...l, quebra: v })} modo="decimal" dica="opcional" />
          <div className="campo"><span>&nbsp;</span><div className="mini-dica">{temQuebra
            ? 'Sai do peso da balança antes da arroba. O romaneio mostra o peso da balança, a quebra e o peso que vale.'
            : 'Só se combinou quebra com o vendedor.'}</div></div>
        </div>
      )}
      <div className="descontos-do-lote">
        <button type="button" className="mini" aria-expanded={descontos} onClick={() => setDescontos(!descontos)}>
          Descontos {descontos ? '▾' : '▸'} <span className="mini-dica">{textoDosDescontos(descontosDoRascunho(l))}</span>
        </button>
        {descontos && (
          <>
            <div className="linha">
              <Campo rotulo={`${NOME_FINANCEIRO} %`} valor={l.fin} mudar={(v) => p.mudar({ ...l, fin: v })} modo="decimal" dica="0" />
              <Campo rotulo={`${NOME_FUNRURAL} %`} valor={l.fun} mudar={(v) => p.mudar({ ...l, fun: v })} modo="decimal" dica="0" />
            </div>
            <div className="mini-dica">Os dois são somados e descontados do bruto deste lote, como se faz no curral: 2% + 1,63% = 3,63%.
              O desconto à vista é o de pagar à vista um preço de prazo. O de Funrural é o repasse do desconto que o frigorífico faz.</div>
          </>
        )}
      </div>
      {p.remover && (
        <div className="lote-rodape">
          <span className="mini-dica">{p.usados ? `${p.usados} registro(s) neste lote` : 'sem registros ainda'}</span>
          {!p.usados && <button type="button" className="mini" onClick={p.remover}>Remover lote</button>}
        </div>
      )}
    </div>
  )
}

/**
 * R1C · A quebra não está em `diferencas` (src/dados/acoes.ts): o que mudou
 * nela vai para o histórico da versão nova por aqui.
 */
function diferencasDeQuebra(antes: Lote[], depois: Lote[]): string[] {
  const d: string[] = []
  for (const l of depois) {
    const a = antes.find((x) => x.id === l.id)
    if (!a) continue
    if (quebraDoLote(a) !== quebraDoLote(l)) d.push(`${l.categoria}: quebra ${paraCampo(quebraDoLote(a))}% → ${paraCampo(quebraDoLote(l))}%`)
  }
  return d
}

/** Lote novo: os descontos vêm do lote anterior (ou, no primeiro, do padrão da ficha/Ajustes). */
export function novoLoteRascunho(base: Base, rendimento: number, d: Descontos): LoteRascunho {
  return { id: novoId(), categoria: '', base, preco: '', rendimento: paraCampo(rendimento), fin: paraCampo(d.financeiroPct || null), fun: paraCampo(d.funruralPct || null) }
}

export function Negociacao({ ficha, ir }: { ficha: Ficha; ir: Ir }) {
  const { ajustes } = usarEstado()
  // 3E: quem não pode mudar a negociação (a Consulta) vê tudo só para leitura,
  // sem «+ Adicionar lote», sem «Salvar nova versão» e sem gravação automática
  // R1A: o comprador não muda a negociação de ficha encerrada (o banco recusa,
  // 013 fichas_guarda) — reabre com motivo; o gestor salva nova versão direto
  const podeEditar = usarPode()('editarNegociacao', { encerrada: ficha.situacao === 'encerrada' })
  // R1C: o rendimento padrão de cada categoria ('' = o geral dos Ajustes)
  const rendimentoDe = (c: string) => rendimentoPadrao(c, ajustes)
  // R2D · Venda do meu estoque: as categorias dos lotes são as do estoque, com o saldo
  const doEstoque = usarCategoriasDoEstoque(ficha)
  const cancelada = ficha.situacao === 'cancelada'
  const encerrada = ficha.situacao === 'encerrada' && podeEditar
  const aberta = ficha.situacao === 'aberta' && podeEditar
  const [forma, setForma] = useState<Forma>(ficha.forma)
  const [lotes, setLotes] = useState<LoteRascunho[]>(() =>
    ficha.lotes.length ? ficha.lotes.map((l) => paraRascunho(l, ficha)) : !podeEditar ? [] : [novoLoteRascunho(ajustes.base, rendimentoPadrao('', ajustes), descontosDoLote({}, ficha))])

  // os % da ficha não aparecem mais: ficam como estão (padrão do lote antigo sem os seus)
  // R1C: lote com rendimento fora de 40 a 60% também fica fora, até corrigir
  const valido = (l: LoteRascunho) => !!l.categoria && !problemaDoLote(l)
  const paraGravar = (l: LoteRascunho) => loteDoRascunho(l, rendimentoDe(l.categoria))
  const depois = { lotes: lotes.filter(valido).map(paraGravar), financeiroPct: ficha.financeiroPct, funruralPct: ficha.funruralPct }
  const previa = calcular({ ...depois, registros: ficha.registros, arrobaEmDuasCasas: ficha.arrobaEmDuasCasas })
  // R1C: lotes que a pessoa tirou nesta tela (não voltam ao gravar)
  const removidos = useRef(new Set<string>())
  /**
   * R1C · Lote que desceu de outro aparelho com a tela aberta (o «+ Novo lote»
   * do curral): aparece aqui também, no fim da lista.
   */
  useEffect(() => {
    const novos = ficha.lotes.filter((l) => !removidos.current.has(l.id) && !lotes.some((x) => x.id === l.id))
    if (novos.length) setLotes((ls) => [...ls, ...novos.filter((l) => !ls.some((x) => x.id === l.id)).map((l) => paraRascunho(l, ficha))])
  }, [ficha.lotes]) // eslint-disable-line react-hooks/exhaustive-deps
  // 3B: porteiras só em ficha de Compra (é onde o gestor aparta o gado do cliente)
  const comPorteiras = ficha.finalidade === 'Compra'
  const [porteiras, setPorteiras] = useState<PorteiraRascunho[]>(() => porteirasDa(ficha).map(paraRascunhoDePorteira))
  const ps = comPorteiras ? porteirasDoRascunho(porteiras) : null
  // as porteiras como estavam ao abrir a tela: mudou → uma linha no histórico, ao sair
  const porteirasAntes = useRef(nomesDasPorteiras(porteirasDa(ficha)))

  /**
   * Os lotes que se gravam sozinhos: os que têm categoria. Lote já gravado
   * cuja categoria foi apagada na tela continua como estava — senão os
   * registros dele sumiriam das contas.
   */
  function lotesParaGravar(f: Ficha): Lote[] {
    const daTela = lotes.flatMap((r) => (valido(r) ? [paraGravar(r)] : f.lotes.filter((l) => l.id === r.id)))
    // R1C: o lote gravado que a tela não conhece (criado no curral por outro
    // aparelho enquanto esta tela estava aberta) fica — com os registros dele
    const desconhecidos = f.lotes.filter((l) => !lotes.some((r) => r.id === l.id) && !removidos.current.has(l.id))
    return [...daTela, ...desconhecidos]
  }

  const { situacao, sair } = usarGravacaoAutomatica({
    ativo: aberta,
    chave: JSON.stringify({ forma, lotes: depois.lotes, porteiras: typeof ps === 'string' ? null : ps }),
    gravar: () => {
      const f = lerEstado().fichas.find((x) => x.id === ficha.id)
      if (!f || f.situacao !== 'aberta') return
      const novosLotes = lotesParaGravar(f)
      // porteira pela metade (cliente sem nome) fica na tela e não é gravada até completar
      const novas = ps && typeof ps !== 'string' ? manterCadastro(ps, porteirasDa(f)) : null
      const igual = f.forma === forma && JSON.stringify(f.lotes) === JSON.stringify(novosLotes)
        && (!novas || emTexto(novas) === emTexto(porteirasDa(f)))
      if (igual) return
      mudarFicha(ficha.id, (x) => {
        x.forma = forma
        x.lotes = novosLotes
        if (novas) x.porteiras = novas
      })
    },
    aoSair: () => {
      mudar((e) => {
        const f = e.fichas.find((x) => x.id === ficha.id)
        if (!f || f.situacao !== 'aberta') return
        if (registrarPorteiras(e, f, porteirasAntes.current)) f.alteradaEm = agora()
      })
      porteirasAntes.current = nomesDasPorteiras(porteirasDa(lerEstado().fichas.find((x) => x.id === ficha.id) ?? ficha))
    },
  })

  /** O que falta para seguir, ou nada. */
  function problema(): string | null {
    if (lotes.some((l) => !l.categoria)) return 'Escolha a categoria de cada lote (ou remova o lote vazio).'
    const ruim = lotes.find((l) => problemaDoLote(l))
    if (ruim) return `${ruim.categoria}: ${problemaDoLote(ruim)}`
    if (!depois.lotes.length) return 'Cadastre ao menos um lote.'
    if (typeof ps === 'string') return ps
    return null
  }

  /** Ficha aberta: tudo já está gravado; termina o que faltou e vai para a pesagem. */
  function proximo() {
    const p = problema()
    if (p) { window.alert(p); return }
    sair()
    ir({ tela: 'ficha', id: ficha.id, aba: 'pesagem' })
  }

  /** Ficha encerrada: gravar é gerar uma versão nova do romaneio — por isso é um botão, com confirmação. */
  async function salvarVersao() {
    const p = problema()
    if (p) { window.alert(p); return }
    const novas = ps && typeof ps !== 'string' && emTexto(ps) !== emTexto(porteirasDa(ficha)) ? ps : null
    const mudou = [...diferencas(negociacaoDe(ficha), depois), ...diferencasDeQuebra(ficha.lotes, depois.lotes)]
    if (!mudou.length && !novas) { avisar('Nada mudou'); return }
    if (mudou.length && !await confirmar({
      titulo: `Gerar a versão ${ficha.versao + 1} do romaneio?`,
      texto: <>A ficha já está encerrada. Muda:<ul>{mudou.map((m) => <li key={m}>{m}</li>)}</ul>Os pesos não mudam.</>,
      sim: `Salvar a versão ${ficha.versao + 1}`,
    })) return
    mudar((e) => {
      const f = e.fichas.find((x) => x.id === ficha.id)
      if (!f) return
      if (mudou.length) {
        // 3E: só versão, lotes e forma — a situação fica «encerrada» e o romaneio, final
        aplicarNovaVersao(f, depois, forma, mudou, agora())
      }
      if (novas) {
        f.porteiras = manterCadastro(novas, porteirasDa(f))
        registrarPorteiras(e, f, porteirasAntes.current)
      }
      f.alteradaEm = agora()
    })
    avisar(mudou.length ? `Versão ${ficha.versao + 1} do romaneio` : 'Porteiras salvas — o romaneio não muda')
    if (mudou.length) ir({ tela: 'ficha', id: ficha.id, aba: 'romaneio' })
  }

  function acrescentarLote() {
    const ultimo = lotes[lotes.length - 1]
    setLotes([...lotes, novoLoteRascunho(ajustes.base, rendimentoDe(''), ultimo ? descontosDoRascunho(ultimo) : descontosDoLote({}, ficha))])
  }

  return (
    <div>
      {encerrada && <Tarja>Ficha encerrada. Corrigir aqui gera uma nova versão do romaneio, sem mexer nos pesos. A versão anterior fica guardada no histórico.</Tarja>}
      <fieldset disabled={cancelada || !podeEditar} className="sem-borda">
        <h2 className="secao">Como os animais serão pesados (padrão)</h2>
        <div className="cartao">
          <Segmento valor={forma} mudar={setForma} desabilitado={encerrada || !podeEditar}
            opcoes={[['curral', 'Balança no curral'], ['caminhao', 'Caminhão no balanção']]} />
          <div className="mini-dica" style={{ marginTop: 8 }}>
            {forma === 'curral'
              ? 'Um ou mais animais por vez na balança do curral. Se aparecer caminhão no meio, troque na própria pesagem, registro a registro.'
              : 'Sem balança no curral: o caminhão é pesado vazio e carregado no balanção. Na pesagem dá para trocar, registro a registro, para balança ou peso líquido.'}
          </div>
        </div>

        <h2 className="secao">Lotes de negociação</h2>
        <Dica>
          Cada lote é um preço. Se houver diferentes categorias sendo negociadas, informe o rendimento de cada uma
          que vai passar no curral. Animais da mesma categoria com preço diferente (ex.: 18 bezerros a R$ 1.900 e 6 a R$ 1.600)
          são dois lotes. O desconto à vista e o de Funrural ficam em <b>Descontos ▸</b>, dentro de cada lote.
        </Dica>
        {doEstoque && (
          <Dica>
            <b>Venda do meu estoque.</b> Os lotes usam as categorias do estoque, com o saldo de hoje:{' '}
            {doEstoque.categorias.map(doEstoque.rotulo).join(' · ')}. Ao encerrar, cada lote sai da categoria dele pelo custo médio
            dela — sem marcar nada depois.
          </Dica>
        )}
        {lotes.map((l, i) => (
          <EditorDeLote key={l.id} lote={l} categorias={doEstoque?.categorias ?? ajustes.categorias} rendimentoDe={rendimentoDe}
            rotuloDaCategoria={doEstoque?.rotulo}
            usados={ficha.registros.filter((r) => r.loteId === l.id).length}
            mudar={(nl) => setLotes(lotes.map((x, j) => (j === i ? nl : x)))}
            remover={podeEditar ? () => { removidos.current.add(l.id); setLotes(lotes.filter((_, j) => j !== i)) } : undefined} />
        ))}
        {!lotes.length && <div className="cartao vazio">Nenhum lote ainda.</div>}
        {podeEditar && <button type="button" className="botao fantasma" onClick={acrescentarLote}>+ Adicionar lote</button>}

        {previa.cab > 0 && (
          <div className="resultado">
            {previa.linhas.filter((l) => l.cab > 0).map((l) => (
              <div className="tl" key={l.lote.id}><span>{nomeDoLote(l.lote)} · {l.cab} cab{l.kg ? ` · ${kg(l.kg)} kg` : ''}</span><b>{reais(l.valor)}</b></div>
            ))}
            <div className="tl"><span>Bruto</span><b>{previa.temPreco ? reais(previa.bruto) : '—'}</b></div>
            {linhasDeDesconto(previa).map((d) => <div className="tl" key={d.rotulo}><span>{d.rotulo}</span><b>− {reais(d.valor)}</b></div>)}
            <div className="tl total"><span>Líquido</span><b>{previa.temPreco ? reais(previa.liquido) : '—'}</b></div>
          </div>
        )}

        {comPorteiras && (podeEditar || porteiras.length > 0) && <EditorDePorteiras ficha={ficha} porteiras={porteiras} mudar={setPorteiras} leitura={!podeEditar} />}
        {comPorteiras && typeof ps === 'string' && aberta && <div className="alerta">{ps} Até lá, as porteiras não são gravadas.</div>}

        {/* 3E: o resumo da condição de pagamento saiu daqui («desnecessário», disse o
            gestor no test drive). A condição continua na aba Pagamento. */}

        {encerrada
          ? <button className="botao grande" onClick={salvarVersao}>Salvar nova versão do romaneio</button>
          : aberta && (
            <div className="rodape-passo">
              <button className="botao grande" onClick={proximo}>Próximo ›</button>
              <SinalDeSalvo situacao={situacao} />
            </div>
          )}
      </fieldset>
      {/* 3E: o cartão da Catira («Entra no meu estoque», «Ver na catira») saiu
          daqui — não faz sentido antes de entrar animal. Mora na aba Custo, depois
          do encerramento, só para o gestor e só com a ferramenta Catira ligada. */}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * 3B · Destinos (porteiras)
 * ------------------------------------------------------------------------- */

interface PorteiraRascunho {
  id: string; tipo: TipoDePorteira; nome: string; municipio: string; uf: string; valorVenda: string
  clienteId?: string; localId?: string
  /** a GTA se anota no Romaneio; aqui só passa adiante, para não se perder */
  gta?: string
}

const MEU_ESTOQUE = 'Meu estoque'

function paraRascunhoDePorteira(p: Porteira): PorteiraRascunho {
  return {
    id: p.id, tipo: p.tipo, nome: p.nome ?? '', municipio: p.municipio ?? '', uf: p.uf ?? '', valorVenda: paraCampo(p.valorVenda ?? null),
    clienteId: p.clienteId, localId: p.localId, gta: p.gta,
  }
}

/** As porteiras em texto, com os campos sempre na mesma ordem: para saber se algo mudou de fato. */
function emTexto(ps: Porteira[]): string {
  return JSON.stringify(ps.map((p) => Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b)))))
}

/** Só o que o histórico diz das porteiras: tipo e nome. */
function nomesDasPorteiras(ps: Porteira[]): string {
  return JSON.stringify(ps.map((p) => [p.tipo, p.nome]))
}

/** As porteiras prontas para gravar, ou a frase do que falta. */
function porteirasDoRascunho(lista: PorteiraRascunho[]): Porteira[] | string {
  const out: Porteira[] = []
  for (const r of lista) {
    const nome = r.nome.trim()
    if (r.tipo === 'cliente' && !nome) return 'Escreva o nome de cada cliente (ou remova a porteira vazia).'
    const p: Porteira = { id: r.id, tipo: r.tipo, nome: nome || (r.tipo === 'estoque' ? MEU_ESTOQUE : '') }
    if (r.tipo === 'cliente' && r.clienteId) p.clienteId = r.clienteId
    if (r.tipo === 'estoque' && r.localId) p.localId = r.localId
    // 3F: o comprador final da revenda também sai do cadastro de clientes
    if (r.tipo === 'revenda' && r.clienteId) p.clienteId = r.clienteId
    if (r.municipio.trim()) p.municipio = r.municipio.trim()
    if (r.uf.trim()) p.uf = r.uf.trim().toUpperCase()
    if (r.tipo === 'revenda') {
      // 3F: o valor total da «Revenda direta» antiga só passa adiante (ficha nova
      // nunca grava): o preço de venda mora fora da ficha, na tabela da 012
      const v = lerNumero(r.valorVenda)
      if (v !== null && v > 0) p.valorVenda = centavos(v)
    }
    if (r.gta?.trim()) p.gta = r.gta.trim()
    out.push(p)
  }
  return out
}

/**
 * A tela não sabe o id que o cadastro deu ao nome novo (ele é dado ao sair) —
 * nem a GTA anotada depois no Romaneio. Mesma porteira e mesmo nome: mantém
 * o que já estava gravado.
 */
function manterCadastro(novas: Porteira[], antigas: Porteira[]): Porteira[] {
  return novas.map((p) => {
    const a = antigas.find((x) => x.id === p.id && x.tipo === p.tipo && normalizar(x.nome) === normalizar(p.nome))
    if (!a) return p
    const q = { ...p }
    if (!q.clienteId && a.clienteId) q.clienteId = a.clienteId
    if (!q.localId && a.localId) q.localId = a.localId
    if (!q.gta && a.gta) q.gta = a.gta
    return q
  })
}

/** O nome no cadastro de produtores, com o marcador. Devolve o id. */
function marcarNoCadastro(e: Estado, p: Porteira, marca: 'cliente' | 'meuLocal'): string {
  const achado = acharProdutorPeloNome(e, p.nome)
  if (achado) {
    if (!achado[marca]) { achado[marca] = true; achado.alteradoEm = agora() }
    return achado.id
  }
  const novo: Produtor = { ...produtorVazio(), nome: p.nome, municipio: p.municipio ?? '', uf: p.uf ?? '' }
  novo[marca] = true
  return guardarProdutor(e, novo).id
}

/**
 * Uma vez, ao sair da tela (ou ao salvar a versão): o cliente e o local
 * próprio entram no cadastro de produtores, marcados — é o mesmo cadastro, a
 * fazenda que vende hoje compra amanhã. «Meu estoque» sem nome não entra.
 * Se as porteiras mudaram desde que a tela abriu, uma linha no histórico.
 * Devolve true se mexeu na ficha.
 */
function registrarPorteiras(e: Estado, f: Ficha, antes: string): boolean {
  const ps = porteirasDa(f)
  const ids = (x: Porteira) => [x.clienteId, x.localId].join('|')
  const eram = ps.map(ids).join(',')
  f.porteiras = ps.map((p) => {
    if (p.tipo === 'cliente') return { ...p, clienteId: marcarNoCadastro(e, p, 'cliente') }
    // 3F: o comprador final da revenda entra no cadastro de clientes (destinatário do romaneio de venda)
    if (p.tipo === 'revenda' && p.nome?.trim()) return { ...p, clienteId: marcarNoCadastro(e, p, 'cliente') }
    if (p.tipo === 'estoque' && normalizar(p.nome) !== normalizar(MEU_ESTOQUE)) return { ...p, localId: marcarNoCadastro(e, p, 'meuLocal') }
    return p
  })
  let mexeu = f.porteiras.map(ids).join(',') !== eram
  if (nomesDasPorteiras(f.porteiras) !== antes) {
    f.historico.push({ em: agora(), oque: `Porteiras: ${f.porteiras.map((p) => nomeDaPorteira(p)).join(', ') || 'nenhuma'}` })
    mexeu = true
  }
  return mexeu
}

function EditorDePorteiras({ ficha, porteiras, mudar: setLista, leitura }: { ficha: Ficha; porteiras: PorteiraRascunho[]; mudar: (l: PorteiraRascunho[]) => void; leitura?: boolean }) {
  const { produtores } = usarEstado()
  const ativos = produtores.filter((x) => x.ativo !== false)
  // clientes de antes primeiro; depois os outros produtores (todo produtor pode virar cliente)
  const paraCliente = ativos.slice().sort((a, b) => Number(!!b.cliente) - Number(!!a.cliente) || a.nome.localeCompare(b.nome, 'pt-BR'))
  // 3D: «Meu estoque» escolhe entre os locais próprios do gestor (sítio, fazenda de recria)
  const locais = ativos.filter((x) => x.meuLocal).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  const salvas = porteirasDa(ficha)
  const verVenda = usarPode()('verVenda')
  // R1C: textos por papel — o comprador não lê sobre comissão, ágio nem revenda
  const comprador = usarPapel() === 'comprador'
  const ajudaDe = (t: TipoDePorteira) => (comprador ? TIPOS_DE_PORTEIRA[t].ajudaComprador : TIPOS_DE_PORTEIRA[t].ajuda)

  /** cabeças e gastos que já estão nesta porteira (na ficha gravada): porteira em uso não sai */
  function uso(id: string): { cab: number; regs: number; gastos: number } {
    const regs = salvas.length ? ficha.registros.filter((r) => porteiraDe(ficha, r.destinoId) === id) : []
    return { cab: regs.filter((r) => !r.pendente).reduce((s, r) => s + r.qtd, 0), regs: regs.length, gastos: ficha.custos.filter((c) => c.destinoId === id).length }
  }
  const mudarUma = (i: number, x: Partial<PorteiraRascunho>) => setLista(porteiras.map((p, j) => (j === i ? { ...p, ...x } : p)))
  function acrescentar(tipo: TipoDePorteira) {
    setLista([...porteiras, { id: novoId(), tipo, nome: tipo === 'estoque' ? MEU_ESTOQUE : '', municipio: '', uf: '', valorVenda: '' }])
  }
  /** Nome escolhido da lista (ou digitado): do cadastro, preenche o município e guarda o id. */
  function escolherNome(i: number, nome: string, lista: Produtor[], campoId: 'clienteId' | 'localId') {
    const achado = lista.find((x) => normalizar(x.nome) === normalizar(nome))
    mudarUma(i, achado ? { nome, [campoId]: achado.id, municipio: achado.municipio, uf: achado.uf } : { nome, [campoId]: undefined })
  }

  return (
    <>
      <h2 className="secao">Porteiras</h2>
      {comprador ? (
        <Dica>
          Para onde o gado vai: <b>Cliente</b> (a fazenda paga o vendedor direto) ou <b>Meu estoque</b> (o gado vai para a
          empresa). Monte aqui, com calma — no curral só se toca a porteira. Com uma porteira só (ou nenhuma), a pesagem fica
          como sempre. O vendedor recebe um romaneio só, com todos os animais: a porteira não aparece para ele. Animais pesados
          sem porteira contam na primeira. O nº da GTA de cada porteira se anota depois da pesagem, no romaneio.
        </Dica>
      ) : (
        <Dica>
          Para onde o gado vai: um cliente (intermediação: ele paga o vendedor direto e você recebe comissão), o seu estoque
          (sítio, fazenda de recria) ou uma revenda (você paga o vendedor e vende ao comprador final, com ágio). Monte aqui, com
          calma — no curral só se toca a porteira. Com uma porteira só (ou nenhuma), a pesagem fica como sempre. O vendedor
          recebe um romaneio só, com todos os animais: a porteira não aparece para ele. Animais pesados sem porteira contam
          na primeira. O nº da GTA de cada porteira se anota depois da pesagem, no romaneio.
        </Dica>
      )}
      {porteiras.map((p, i) => {
        const u = uso(p.id)
        const lista = p.tipo === 'estoque' ? locais : paraCliente
        const doCadastro = !!p.nome.trim() && lista.some((x) => normalizar(x.nome) === normalizar(p.nome))
        const generico = p.tipo === 'estoque' && normalizar(p.nome) === normalizar(MEU_ESTOQUE)
        return (
          <div className="lote" key={p.id}>
            <div className="item-topo">
              <b>{i + 1}. {TIPOS_DE_PORTEIRA[p.tipo].nome}</b>
              {i === 0 && porteiras.length > 1 && <span className="etiqueta neutra">primeira</span>}
            </div>
            <div className="mini-dica" style={{ marginTop: 0 }}>{ajudaDe(p.tipo)}</div>
            {p.tipo === 'cliente' && (
              <>
                <Campo rotulo="Cliente (fazenda ou pessoa)" valor={p.nome} mudar={(v) => escolherNome(i, v, paraCliente, 'clienteId')} lista="clientes-porteira" dica="ex.: Fazenda Boa Vista" />
                <AvisoDeNome nome={p.nome} doCadastro={doCadastro} parecido={nomeParecido(paraCliente, p.nome)?.nome}
                  usar={(v) => escolherNome(i, v, paraCliente, 'clienteId')} novo="Cliente novo: entra no cadastro de produtores, marcado como cliente." />
              </>
            )}
            {p.tipo === 'estoque' && (
              <>
                <Campo rotulo="Local (seu sítio, sua fazenda)" valor={p.nome} mudar={(v) => escolherNome(i, v, locais, 'localId')} lista="locais-porteira" dica={MEU_ESTOQUE} />
                {generico
                  ? <div className="mini-dica">Sem nome de lugar: fica «Meu estoque». Escolha um dos seus locais, se quiser dizer qual.</div>
                  : <AvisoDeNome nome={p.nome} doCadastro={doCadastro} parecido={nomeParecido(locais, p.nome)?.nome}
                      usar={(v) => escolherNome(i, v, locais, 'localId')} novo="Local novo: entra no cadastro como local seu." />}
              </>
            )}
            {p.tipo === 'revenda' && (
              <>
                <Campo rotulo="Comprador final" valor={p.nome} mudar={(v) => escolherNome(i, v, paraCliente, 'clienteId')} lista="clientes-porteira" dica="ex.: Frigorífico Bom Corte" />
                <AvisoDeNome nome={p.nome} doCadastro={doCadastro} parecido={nomeParecido(paraCliente, p.nome)?.nome}
                  usar={(v) => escolherNome(i, v, paraCliente, 'clienteId')} novo="Comprador novo: entra no cadastro de produtores, marcado como cliente." />
              </>
            )}
            {!generico && (
              <div className="linha">
                <div style={{ flex: 3 }}><Campo rotulo="Município" valor={p.municipio} mudar={(v) => mudarUma(i, { municipio: v })} dica="opcional" /></div>
                <Campo rotulo="UF" valor={p.uf} mudar={(v) => mudarUma(i, { uf: v })} max={2} maiusculas />
              </div>
            )}
            {/* 3F: o preço de venda, só para quem vê (gestor e Consulta) e só com a porteira já gravada */}
            {p.tipo === 'revenda' && verVenda && salvas.some((x) => x.id === p.id && x.tipo === 'revenda') && (
              <PrecoDeVendaComChave ficha={ficha} destinoId={p.id} valorAntigo={lerNumero(p.valorVenda)}
                esquecerValorAntigo={() => mudarUma(i, { valorVenda: '' })} />
            )}
            {p.tipo === 'revenda' && verVenda && !salvas.some((x) => x.id === p.id && x.tipo === 'revenda') && (
              <div className="mini-dica">O preço de venda se monta assim que a porteira estiver gravada.</div>
            )}
            <div className="lote-rodape">
              <span className="mini-dica">{u.regs ? `${u.cab} cab em ${u.regs} registro(s)` : 'sem animais ainda'}{u.gastos ? ` · ${u.gastos} gasto(s)` : ''}</span>
              {!leitura && !u.regs && !u.gastos && <button type="button" className="mini" onClick={() => setLista(porteiras.filter((_, j) => j !== i))}>Remover</button>}
            </div>
          </div>
        )
      })}
      <datalist id="clientes-porteira">{paraCliente.map((x) => <option key={x.id} value={x.nome} />)}</datalist>
      <datalist id="locais-porteira">{locais.map((x) => <option key={x.id} value={x.nome} />)}</datalist>
      {!leitura && <div className="barra">
        <button type="button" className="botao fantasma pequeno" onClick={() => acrescentar('cliente')}>+ Cliente</button>
        <button type="button" className="botao fantasma pequeno" onClick={() => acrescentar('estoque')}>+ Meu estoque</button>
        {/* R1C: «+ Revenda» só para quem vê o preço de venda (o comprador não) */}
        {verVenda && <button type="button" className="botao fantasma pequeno" onClick={() => acrescentar('revenda')}>+ Revenda</button>}
      </div>}
      {!leitura && porteiras.length > 0 && ficha.situacao === 'encerrada' && <div className="mini-dica">Ficha encerrada: as porteiras se gravam no botão abaixo. Mudar porteira não gera versão nova do romaneio.</div>}
    </>
  )
}

/* ---------------------------------------------------------------------------
 * 3F · Preço de venda da porteira Revenda
 * ------------------------------------------------------------------------- */

/** Remonta o formulário quando a tabela gravada muda (salvou aqui, ou desceu de outro aparelho). */
function PrecoDeVendaComChave(p: Parameters<typeof PrecoDeVenda>[0]) {
  const pg = usarPagamentos()
  const v = vendaDaPorteira(pg, p.ficha.id, p.destinoId)
  return <PrecoDeVenda key={v?.lancadaEm ?? 'nova'} {...p} />
}

interface LinhaDeVenda { loteId: string; base: Base; preco: string; rendimento: string; fin: string; fun: string }

function paraLinhaDeVenda(v: LoteDeVenda): LinhaDeVenda {
  return { loteId: v.loteId, base: v.base, preco: paraCampo(v.preco), rendimento: paraCampo(v.rendimento), fin: paraCampo(v.financeiroPct || null), fun: paraCampo(v.funruralPct || null) }
}

function deLinhaDeVenda(l: LinhaDeVenda): LoteDeVenda {
  const preco = lerNumero(l.preco)
  return {
    loteId: l.loteId, base: l.base, preco: preco !== null && preco > 0 ? centavos(preco) : null,
    rendimento: Math.min(100, Math.max(0, lerNumero(l.rendimento) || 50)),
    financeiroPct: Math.min(100, Math.max(0, lerNumero(l.fin) ?? 0)), funruralPct: Math.min(100, Math.max(0, lerNumero(l.fun) ?? 0)),
  }
}

/**
 * Os MESMOS lotes da compra, cada um com o preço ao comprador final: base
 * (arroba, kg ou cabeça), rendimento e descontos da venda, no padrão da 3D.
 * Monta-se com calma, antes ou depois do curral; no curral nada muda. O lote
 * novo copia os descontos do lote de venda anterior.
 */
function PrecoDeVenda({ ficha, destinoId, valorAntigo, esquecerValorAntigo }: {
  ficha: Ficha; destinoId: string; valorAntigo: number | null; esquecerValorAntigo: () => void
}) {
  const pg = usarPagamentos()
  const pode = usarPode()
  const podeGravar = pode('precoDeVenda') && ficha.situacao !== 'cancelada'
  const gravada = vendaDaPorteira(pg, ficha.id, destinoId)
  const pendente = pg.pendentes.vendas.includes(`${ficha.id}|${destinoId}`)
  const [aberta, setAberta] = useState(false)
  const [linhas, setLinhas] = useState<LinhaDeVenda[]>(() => {
    const out: LinhaDeVenda[] = []
    for (const l of ficha.lotes) {
      const v = gravada?.lotes.find((x) => x.loteId === l.id)
      const anterior = out.length ? deLinhaDeVenda(out[out.length - 1]!) : null
      out.push(paraLinhaDeVenda(v ?? loteDeVendaPadrao(l, anterior)))
    }
    return out
  })
  const [obs, setObs] = useState(gravada?.obs ?? '')
  const mudarLinha = (i: number, x: Partial<LinhaDeVenda>) => setLinhas(linhas.map((l, j) => (j === i ? { ...l, ...x } : l)))

  function salvar() {
    const lotes = linhas.filter((l) => ficha.lotes.some((x) => x.id === l.loteId)).map(deLinhaDeVenda)
    lancarVenda(ficha.id, destinoId, lotes, obs.trim())
    // a porteira antiga guardava o valor total DENTRO da ficha (que o comprador
    // recebe): com preço por lote, ele sai do documento e da tela
    if (valorAntigo !== null) {
      mudarFicha(ficha.id, (f) => {
        f.porteiras = porteirasDa(f).map((p) => {
          if (p.id !== destinoId) return p
          const { valorVenda: _v, ...resto } = p
          return resto
        })
      })
      esquecerValorAntigo()
    }
    avisar(gravada ? 'Preço de venda corrigido' : 'Preço de venda salvo')
  }

  return (
    <div className="descontos-do-lote" style={{ marginTop: 8 }}>
      <button type="button" className="mini" aria-expanded={aberta} onClick={() => setAberta(!aberta)}>
        Preço de venda {aberta ? '▾' : '▸'} <span className="mini-dica">{gravada ? `${gravada.lotes.filter((l) => l.preco).length} lote(s) com preço` : valorAntigo ? `por valor total: ${reais(valorAntigo)}` : 'ainda sem preço'}</span>
      </button>
      {aberta && (
        <fieldset disabled={!podeGravar} className="sem-borda">
          <Dica>
            O preço ao comprador final, lote a lote, com os mesmos animais e os mesmos pesos da compra. Só você e a Consulta veem:
            o vendedor nunca vê o preço de venda, o comprador final nunca vê o de compra, e o comprador (funcionário) não vê nem a venda nem o ágio.
          </Dica>
          {valorAntigo !== null && !gravada && <Tarja>Porteira antiga: vendida por valor total ({reais(valorAntigo)}). Ao salvar o preço por lote, o valor total deixa de valer.</Tarja>}
          {!ficha.lotes.length && <div className="mini-dica">Cadastre os lotes da compra primeiro.</div>}
          {linhas.map((l, i) => {
            const compra = ficha.lotes.find((x) => x.id === l.loteId)
            if (!compra) return null
            return (
              <div className="lote" key={l.loteId}>
                <div className="item-topo"><b>{nomeDoLote(compra)}</b><span className="etiqueta neutra">compra</span></div>
                <div className="linha">
                  <Escolha rotulo="Base da venda" valor={l.base} mudar={(v) => mudarLinha(i, { base: v })}
                    opcoes={[['arroba', 'Por arroba'], ['kg', 'Por kg vivo'], ['cabeca', 'Por cabeça']]} />
                  <Campo rotulo={`Preço de venda (${BASES[l.base].unidade})`} valor={l.preco} mudar={(v) => mudarLinha(i, { preco: v })} modo="decimal" dica={UNIDADE_DO_PRECO[l.base]} />
                </div>
                <div className="linha">
                  {l.base === 'arroba'
                    ? <Campo rotulo="Rendimento da venda (%)" valor={l.rendimento} mudar={(v) => mudarLinha(i, { rendimento: v })} modo="decimal" dica="50" />
                    : <div className="campo"><span>&nbsp;</span><div className="mini-dica">{l.base === 'cabeca' ? 'Por cabeça: sem rendimento.' : 'Por kg: sem rendimento.'}</div></div>}
                  <Campo rotulo="Desconto à vista %" valor={l.fin} mudar={(v) => mudarLinha(i, { fin: v })} modo="decimal" dica="0" />
                  <Campo rotulo="Desconto de Funrural %" valor={l.fun} mudar={(v) => mudarLinha(i, { fun: v })} modo="decimal" dica="0" />
                </div>
              </div>
            )
          })}
          <Campo rotulo="Observação" valor={obs} mudar={setObs} dica="ex.: combinado por telefone" />
          {podeGravar && ficha.lotes.length > 0 && <button type="button" className="botao" onClick={salvar}>Salvar preço de venda</button>}
          {pendente && <div className="mini-dica">Guardado neste aparelho — vai no próximo Sincronizar.</div>}
          <div className="mini-dica">Mudar o preço de venda não gera versão nova do romaneio de compra. Os descontos da venda são somados e tirados do bruto de cada lote, como na compra.</div>
        </fieldset>
      )}
    </div>
  )
}
